/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';

import {
  PaymentBatchDto,
  PaymentDto,
  PaymentVopMatchStatus,
} from '@alpha/payments-dtos';
import {
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@alpha/ui-lib/ui/external';
import { Loader } from '@alpha/ui-lib/ui/Loader';
import {
  StyledStatSummary,
  TStyledStatSummaryData,
} from '@alpha/ui-lib/ui/StatSummary/StyledStatSummary';
import { Status } from '@alpha/ui-lib/ui/Status';
import {
  IStyledGenericTableProps,
  StyledGenericTable,
} from '@alpha/ui-lib/ui/table';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import t from 'utils/translationHelper';

import { CurrencyPair } from '../../../../../components/International';
import {
  formatCurrency,
  formatNumber,
} from '../../../../../utils/currency.helpers';
import formatIsoDate from '../../../../../utils/formatIsoDate';

import useStyles from './ValidPayments.styles';
import VOPStatus from 'components/Payments/VOP/VOPStatus';
import ValidPaymentsVOPDropdown from './ValidPaymentsVOPDropdown';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import VOPConfirmationModal from 'components/Payments/VOP/VOPConfirmationModal/VOPConfirmationModal';
import DeleteDraftPaymentModal from 'components/Molecules/Payments/DeleteDraftPaymentModal';

interface IValidPaymentProps {
  payments: PaymentDto[];
  batchSummary: PaymentBatchDto;
  loading: boolean;
  setVopRequired?: React.Dispatch<React.SetStateAction<boolean>>;
  refetchValidPayments?: () => void;
  refetchAllPayments?: () => void;
}

const ValidPayments: React.FC<IValidPaymentProps> = ({
  payments,
  batchSummary,
  loading,
  setVopRequired,
  refetchValidPayments,
  refetchAllPayments,
}: IValidPaymentProps) => {
  const sb = useAlphaSnackbar();
  const styles = useStyles();

  const [paymentsTableData, setPaymentsTableData] = useState<any[]>([]);
  const [showTable, setShowTable] = useState<boolean>(true);
  const [validStatsSummary, setValidStatsSummary] = useState<
    TStyledStatSummaryData[]
  >([]);
  const [selectedPayment, setSelectedPayment] = useState<
    PaymentDto | undefined
  >(undefined);
  const [deletePayment, setDeletePayment] = useState<
    PaymentDto | undefined
  >(undefined);

  let columns: IStyledGenericTableProps['columns'] = [
    {
      header: '',
      accessor: 'warning',
      align: 'center',
    },
    {
      header: t('row'),
      accessor: 'row',
    },
    {
      header: t('contract_number'),
      accessor: 'contractNumber',
    },
    {
      header: t('transaction_date'),
      accessor: 'transactionDate',
    },
    {
      header: t('amount'),
      accessor: 'amount',
      align: 'right',
    },
    {
      header: t('value_date'),
      accessor: 'valueDate',
    },
    {
      header: t('beneficiary_name'),
      accessor: 'beneficiaryName',
    },
    {
      header: t('reference'),
      accessor: 'reference',
    },
    {
      header: t('purpose'),
      accessor: 'purpose',
    },
    {
      header: t('verification'),
      accessor: 'verification',
      align: 'center',
    },
    {
      header: t('action'),
      accessor: 'action',
      align: 'center',
    },
  ];

  const hasVopRequiredPayments = payments.some(
    (payment) => payment.vopRequired
  ); // Ideally all payments in a batch shares the same status
  const hasVopToBeVerified = payments.some(
    (payment) => payment.vopRequired
      && !payment.vopDetails?.vopApprovalAcknowledged,
  );

  useEffect(() => {
    setVopRequired?.(hasVopToBeVerified);
  }, [hasVopToBeVerified]);

  if (!hasVopToBeVerified) {
    columns = columns.filter(
      (column) =>  column.accessor !== 'warning'
    );
  }

  if (!hasVopRequiredPayments) {
    columns = columns.filter(
      (column) =>
        column.accessor !== 'verification' && column.accessor !== 'row'
    );
  }

  const createValidStatSummaryData = () => {
    const validSummaryStat: TStyledStatSummaryData[] = [
      {
        key: t('currency'),
        value: (
          <div className={styles.statValue}>
            <CurrencyPair currencyCode={batchSummary.currencyCode} />
          </div>
        ),
      },
      {
        key: t('total_payment_amount'),
        value: (
          <div className={styles.statValue}>{`${
            formatCurrency(
              batchSummary.currencyCode,
              batchSummary.totalValidAmount
            ) || '-'
          }`}</div>
        ),
      },
    ];
    setValidStatsSummary(validSummaryStat);
  };

  const updateValidPaymentsTableData = (validPayments: PaymentDto[]) => {

    const paymentNeedsVerified = (payment: PaymentDto) => {
      return payment.vopRequired && !payment.vopDetails?.vopApprovalAcknowledged;
    }
    const highlightRow = (children: any, payment: PaymentDto) => {
      const highlight = paymentNeedsVerified(payment);
      return (
        <div className={highlight ? styles.hightlight : undefined}>
          {children}
        </div>
      );
    };

    const resultTableData = validPayments.map((payment) => ({
      ...payment,
      warning: paymentNeedsVerified(payment) ? highlightRow(
        <Tooltip title={t('vop_acknowledge_required')} placement="bottom-start" classes={{ tooltip: styles.tooltip }}>
          <div>
         <FontAwesomeIcon icon={faExclamationCircle} size='sm' />
        </div>
        </Tooltip>,
        payment
      ) : <></>,
      row: highlightRow(
        payment.row !== undefined ? <span style={{fontWeight: 'bold'}}>{payment.row + 1}</span> : '',
        payment
      ),
      contractNumber: highlightRow(
        <span className={styles.greenValue}>{payment.contractNumber}</span>,
        payment
      ),
      transactionDate: highlightRow(
        formatIsoDate(payment.uploadedDate) || '-',
        payment
      ),
      amount: highlightRow(
        <span style={{fontWeight: 'bold'}}>{payment.currency + ' ' + formatNumber(payment.amount, 2) || '-'}</span>,
        payment
      ),
      valueDate: highlightRow(formatIsoDate(payment.valueDate) || '-', payment),
      beneficiaryName: highlightRow(
        <span className={styles.greenValue}>{payment.beneficiaryName}</span>,
        payment
      ),
      reference: highlightRow(payment.reference || '-', payment),
      purpose: highlightRow(payment.purpose || '-', payment),
      verification: payment.vopRequired ? (
        <VOPStatus
          payment={payment}
          setSelectedPayment={setSelectedPayment}
        />
      ) : (
        <></>
      ),
      action: <ValidPaymentsVOPDropdown payment={payment} 
      setSelectedPayment={setSelectedPayment} setDeletePayment={setDeletePayment}/>,
    }));

    setPaymentsTableData(resultTableData);
  };

  useEffect(() => {
    if (payments) {
      updateValidPaymentsTableData(payments);
      createValidStatSummaryData();
    }
  }, [payments]);

  return (
    <Box className={styles.validBox}>
        <>
          <div className={styles.validTitleContainer}>
            <IconButton
              onClick={() => setShowTable(!showTable)}
              className={styles.chevron}
              size='small'
            >
              <FontAwesomeIcon
                className={showTable ? styles.iconUp : styles.iconDown}
                icon={faChevronDown as IconProp}
                size='sm'
              />
            </IconButton>
            <FontAwesomeIcon
              icon={faCircleCheck as IconProp}
              className={styles.validIcon}
            />
            <Typography className={styles.validTitle}>{`${t(
              'valid_payments'
            )} (${payments.length})`}</Typography>
            {!hasVopToBeVerified ? (
              <Status variant='success' className={styles.status}>
                {t('ready_to_submit')}
              </Status>
            ) : (
              <></>
            )}
          </div>
          <Typography className={styles.subtitle}>
            {t('these_payments_passed_validation_and_can_be_submitted')}
            {hasVopToBeVerified
              ? t('vop_however_acknowledge_verification_required')
              : ''}
          </Typography>
          <Collapse in={showTable}>
            <div className={styles.statContainer}>
              <StyledStatSummary data={validStatsSummary} />
            </div>
            <div className={styles.tableContainer}>
              <StyledGenericTable
                testId='trades-breakdown-table'
                columns={columns}
                data={paymentsTableData}
                className={styles.table}
              />
            </div>
          </Collapse>
          
          <VOPConfirmationModal
            open={!!selectedPayment}
            readOnly={selectedPayment?.vopDetails?.vopApprovalAcknowledged}
            handleClose={() => setSelectedPayment(undefined)}
            handleAcknowledged={() => {
              setSelectedPayment(undefined);
              sb.trigger(t('vop_verification_acknowledged'), 'success');
              refetchValidPayments?.();
            }}
            payment={selectedPayment}
          />

          <DeleteDraftPaymentModal
            paymentIds={deletePayment ? [deletePayment.id]: []}
            open={!!deletePayment}
            handleClose={() => setDeletePayment(undefined)}
            handleReloadPaymentTable={()=>{
              refetchValidPayments?.();
              refetchAllPayments?.();
            }}
          />
        </>
    </Box>
  );
};

export default ValidPayments;
